/**
 * Redaction for free-text captured console output before it is attached to
 * an issue report (lane tech-feedback-widget, round 2 review fix).
 *
 * `lightConsoleCapture.ts` (QuickFeedbackWidget's proportionate
 * console.error buffer) captures the exact text of whatever a technician's
 * app logged — including, potentially, an email address a page printed for
 * debugging, a Bearer token from a failed fetch, or a raw API key. This
 * module redacts those shapes before the text ever enters the buffer, so it
 * never leaves the device.
 *
 * The server (`functions/src/issueTriage/redactSensitiveText.ts`) applies
 * the same patterns independently as defence in depth — the callable is
 * directly invokable and cannot trust any particular client to have redacted
 * first. The two files are deliberately NOT shared code; see that file's
 * header for why.
 */

// Local part and domain are BOUNDED: unbounded, a long run of "a." made this quadratic (#7644 review).
import { scrubTokenShapes } from '../contextCapture/sanitizer';

const EMAIL_PATTERN = /[a-zA-Z0-9._%+-]{1,64}@[a-zA-Z0-9.-]{1,255}\.[a-zA-Z]{2,}/g;
const JWT_PATTERN = /\b[A-Za-z0-9_-]{10,512}\.[A-Za-z0-9_-]{10,512}\.[A-Za-z0-9_-]{10,512}\b/g;
// The value must look like a token, so "Bearer authentication failed" stays readable.
const BEARER_PATTERN =
  /\bBearer\s+(?=[A-Za-z0-9\-._~+/]*[0-9._~+/-]|[A-Za-z0-9\-._~+/]{20})[A-Za-z0-9\-._~+/]+=*/gi;
const HEX_SECRET_PATTERN = /\b[A-Fa-f0-9]{32,}\b/g;
const BASE64_SECRET_PATTERN = /\b[A-Za-z0-9+/]{40,}={0,2}/g;

/** Hard ceiling on what one call scans; callers store a few thousand characters at most. */
export const MAX_REDACT_INPUT_CHARS = 100_000;

/**
 * Redact email addresses, JWTs, Bearer tokens, and long hex/base64 secrets
 * from a free-text string. Order matters: JWT and Bearer patterns run before
 * the generic hex/base64 patterns so a token is tagged with its specific
 * kind rather than falling through to the generic "[REDACTED_SECRET]".
 */
export function redactSensitiveText(text: string): string {
  if (typeof text !== 'string' || text.length === 0) return text;
  const bounded = text.length > MAX_REDACT_INPUT_CHARS ? text.slice(0, MAX_REDACT_INPUT_CHARS) : text;
  // The sign-in token / credential-field rules run FIRST (as on the server): the old base64 rule would otherwise split
  // an `AMf-vB...` token at its `-` and `_`, leaving fragments the token shapes can no longer recognise. Their markers
  // are skipped by the long-standing rules below. Input is bounded: this runs on whatever the page logged.
  return scrubTokenShapes(bounded)
    .replace(EMAIL_PATTERN, '[REDACTED_EMAIL]')
    .replace(JWT_PATTERN, '[REDACTED_JWT]')
    .replace(BEARER_PATTERN, 'Bearer [REDACTED]')
    .replace(HEX_SECRET_PATTERN, '[REDACTED_SECRET]')
    .replace(BASE64_SECRET_PATTERN, '[REDACTED_SECRET]');
}
